import React, { useEffect, useState } from "react"
import Layout from "@/components/layouts/DefaultLayout"

// Platform icons as inline SVGs
const MacIcon = () => (
  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
)

const WindowsIcon = () => (
  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 12V6.75L9 5.43V11.91L3 12M20 3V11.75L10 11.9V5.21L20 3M3 13L9 13.09V19.9L3 18.75V13M20 13.25V22L10 20.09V13.1L20 13.25Z" />
  </svg>
)

const LinuxIcon = () => (
  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2M12 4C16.4 4 20 7.6 20 12C20 16.4 16.4 20 12 20C7.6 20 4 16.4 4 12C4 7.6 7.6 4 12 4M12 6C8.7 6 6 8.7 6 12C6 15.3 8.7 18 12 18C15.3 18 18 15.3 18 12C18 8.7 15.3 6 12 6M12 8C14.2 8 16 9.8 16 12C16 14.2 14.2 16 12 16C9.8 16 8 14.2 8 12C8 9.8 9.8 8 12 8Z" />
  </svg>
)

type Platform = {
  id: string
  name: string
  icon: React.FC
  yamlUrl: string
  downloads: {
    id: string
    name: string
    urlIndex: number
  }[]
  userAgentMatch: RegExp
}

const platforms: Platform[] = [
  {
    id: "windows",
    name: "Windows",
    icon: WindowsIcon,
    yamlUrl: "https://jds-core-releases.nyc3.digitaloceanspaces.com/updates/latest-windows.yml",
    downloads: [{ id: "exe", name: "Installer", urlIndex: 0 }],
    userAgentMatch: /Windows/i,
  },
  {
    id: "mac",
    name: "macOS",
    icon: MacIcon,
    yamlUrl: "https://jds-core-releases.nyc3.digitaloceanspaces.com/updates/latest-mac.yml",
    downloads: [
      { id: "intel", name: "Intel Mac", urlIndex: 1 },
      { id: "arm", name: "Apple Silicon", urlIndex: 0 },
    ],
    userAgentMatch: /Macintosh/i,
  },
  {
    id: "linux",
    name: "Linux",
    icon: LinuxIcon,
    yamlUrl: "https://jds-core-releases.nyc3.digitaloceanspaces.com/updates/latest-linux.yml",
    downloads: [
      { id: "appimage", name: "AppImage", urlIndex: 0 },
      { id: "deb", name: ".deb", urlIndex: 1 },
    ],
    userAgentMatch: /Linux/i,
  },
]

type ReleaseInfo = {
  version: string
  downloads: {
    [platformId: string]: {
      [downloadId: string]: string
    }
  }
}

const DownloadNativeApp = () => {
  const [releaseInfo, setReleaseInfo] = useState<ReleaseInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [showAllPlatforms, setShowAllPlatforms] = useState(false)
  const [userPlatform, setUserPlatform] = useState<string | null>(null)

  useEffect(() => {
    // Detect user's platform
    const userAgent = navigator.userAgent
    const detectedPlatform = platforms.find((platform) => platform.userAgentMatch.test(userAgent))
    setUserPlatform(detectedPlatform?.id || null)
  }, [])

  useEffect(() => {
    const fetchReleaseInfo = async () => {
      try {
        const baseUrl = "https://jds-core-releases.nyc3.digitaloceanspaces.com/updates/"
        const yamlResponses = await Promise.all(
          platforms.map((platform) => fetch(platform.yamlUrl))
        )

        // Get successful responses only
        const successfulResponses = yamlResponses.filter((response) => response.ok)
        if (successfulResponses.length === 0) {
          // If no responses are successful, set empty release info to show "Coming Soon" for all platforms
          setReleaseInfo({ version: "", downloads: {} })
          setLoading(false)
          return
        }

        const yamlTexts = await Promise.all(successfulResponses.map((response) => response.text()))
        const versions = yamlTexts
          .map((text) => text.match(/version: ([^\n]+)/)?.[1])
          .filter(Boolean)

        // If we have any versions, use the first one
        const version = versions[0] || ""

        const downloads: ReleaseInfo["downloads"] = {}

        // Create a map of platform indices to their successful response indices
        const platformToResponseMap = new Map<number, number>()
        let successfulIndex = 0
        yamlResponses.forEach((response, platformIndex) => {
          if (response.ok) {
            platformToResponseMap.set(platformIndex, successfulIndex)
            successfulIndex++
          }
        })

        // Process each platform
        platforms.forEach((platform, platformIndex) => {
          const response = yamlResponses[platformIndex]
          if (!response.ok) return

          const responseIndex = platformToResponseMap.get(platformIndex)
          if (responseIndex === undefined) return

          const yamlText = yamlTexts[responseIndex]
          if (!yamlText) return

          downloads[platform.id] = {}

          // Find all URL matches in the YAML
          const urlMatches = yamlText.match(/url: ([^\n]+)/g) || []

          // Process each download type for this platform
          platform.downloads.forEach((download) => {
            const urlMatch = urlMatches[download.urlIndex]
            if (urlMatch) {
              const url = urlMatch.match(/url: ([^\n]+)/)?.[1]
              if (url) {
                downloads[platform.id][download.id] = baseUrl + encodeURIComponent(url)
              }
            }
          })
        })

        setReleaseInfo({ version, downloads })
        setLoading(false)
      } catch (error) {
        console.error("Failed to fetch release info:", error)
        // On error, set empty release info to show "Coming Soon" for all platforms
        setReleaseInfo({ version: "", downloads: {} })
        setLoading(false)
      }
    }

    fetchReleaseInfo()
  }, [])

  const visiblePlatforms = showAllPlatforms
    ? platforms
    : platforms.filter((platform) => platform.id === userPlatform)

  return (
    <Layout>
      <div className="flex w-full flex-1 py-32">
        <div className="container mx-auto max-w-4xl px-4">
          <h1 className="mb-4 text-2xl font-bold">Get Core Desktop</h1>
          <p className="mb-8 text-silver">
            All the power of core.jdslabs.com, plus automatic device connectivity, offline control,
            and native communication.
          </p>

          <div className="space-y-8">
            <div className="rounded-lg bg-dark p-6">
              {loading ? (
                <div className="text-silver">Loading download links...</div>
              ) : releaseInfo ? (
                <div className="flex flex-col gap-4">
                  {visiblePlatforms.map((platform) => (
                    <div key={platform.id}>
                      <h3 className="type-heading-sm mb-4 font-bold text-white">{platform.name}</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {platform.downloads.map((download) => {
                          const url = releaseInfo?.downloads?.[platform.id]?.[download.id]

                          return (
                            <a
                              key={download.id}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center justify-center rounded-plus px-6 py-3 font-medium transition-colors ${
                                url
                                  ? "bg-[#232528] text-silver hover:text-white"
                                  : "cursor-not-allowed bg-[#232528]/50 text-silver/50"
                              }`}
                              onClick={(e) => !url && e.preventDefault()}
                            >
                              <platform.icon />
                              {url
                                ? `Download for ${download.name} (v${releaseInfo.version})`
                                : `${download.name} - Coming Soon`}
                            </a>
                          )
                        })}
                      </div>
                    </div>
                  ))}

                  {!showAllPlatforms && (
                    <button
                      onClick={() => setShowAllPlatforms(true)}
                      className="mt-4 text-sm text-silver hover:text-white"
                    >
                      Show other platforms?
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-silver">Loading download links...</div>
              )}
            </div>

            <div className="rounded-lg bg-dark p-6">
              <h2 className="mb-4 text-xl font-semibold text-white">Why go native?</h2>
              <p className="mb-6 text-silver">
                Core Desktop is the best way to experience your Element IV from a desktop system:
              </p>
              <ul className="ml-4 list-disc space-y-2 text-silver">
                <li>Automatically connects to your Element IV</li>
                <li>No browser requirements or permissions prompts</li>
                <li>Enables offline control (or login and enjoy community presets)</li>
                <li>Supports toggling between multiple Element IVs from one system</li>
                <li>Easier firmware updates</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default DownloadNativeApp
